import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import CookieConsent from "./components/CookieConsent";

// Context
import { AuthProvider, useAuth } from "./context/AuthContext";

// Pages
import Landing from "./pages/Landing";
import BusinessCard from "./pages/BusinessCard";
import Presentation from "./pages/Presentation";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import VerifyEmail from "./pages/VerifyEmail";
import VerifyEmailPending from "./pages/VerifyEmailPending";
import SelectPlan from "./pages/SelectPlan";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import Reviews from "./pages/Reviews";
import QRCode from "./pages/QRCode";
import KPIs from "./pages/KPIs";
import ActionPlan from "./pages/ActionPlan";
import WeeklyReports from "./pages/WeeklyReports";
import Subscription from "./pages/Subscription";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import Invoices from "./pages/Invoices";
import Settings from "./pages/Settings";
import PublicReview from "./pages/PublicReview";
import GoogleSetupGuide from "./pages/GoogleSetupGuide";
import { MentionsLegales, CGU, CGV, PolitiqueConfidentialite, APropos, FAQ, Cookies, Contact, RGPD } from "./pages/LegalPages";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminOrganizations from "./pages/admin/AdminOrganizations";
import AdminInvoices from "./pages/admin/AdminInvoices";
import AdminSubscriptions from "./pages/admin/AdminSubscriptions";
import AdminSettings from "./pages/admin/AdminSettings";

// Layout
import AppLayout from "./components/layout/AppLayout";
import AdminLayout from "./components/layout/AdminLayout";

import "./App.css";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
      </div>
    );
  }
  
  if (!user || !user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Redirect if authenticated
const PublicOnlyRoute = ({ children }) => {
  const { user, organization, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
      </div>
    );
  }
  
  if (user) {
    // If user has no organization, redirect to select-plan (new user flow)
    if (!organization && !user.is_admin) {
      return <Navigate to="/select-plan" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/carte-visite" element={<BusinessCard />} />
      <Route path="/card" element={<BusinessCard />} />
      <Route path="/presentation" element={<Presentation />} />
      <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
      <Route path="/signup" element={<PublicOnlyRoute><Signup /></PublicOnlyRoute>} />
      <Route path="/review/:orgId" element={<PublicReview />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/verify-email-pending" element={<VerifyEmailPending />} />
      <Route path="/guide/google-setup" element={<GoogleSetupGuide />} />
      
      {/* Legal Pages */}
      <Route path="/mentions-legales" element={<MentionsLegales />} />
      <Route path="/cgu" element={<CGU />} />
      <Route path="/cgv" element={<CGV />} />
      <Route path="/terms" element={<CGV />} />
      <Route path="/confidentialite" element={<PolitiqueConfidentialite />} />
      <Route path="/privacy" element={<PolitiqueConfidentialite />} />
      <Route path="/a-propos" element={<APropos />} />
      <Route path="/about" element={<APropos />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/rgpd" element={<RGPD />} />
      
      {/* Protected Routes */}
      <Route path="/select-plan" element={<ProtectedRoute><SelectPlan /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      
      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/reviews" element={<Reviews />} />
        <Route path="/qrcode" element={<QRCode />} />
        <Route path="/kpis" element={<KPIs />} />
        <Route path="/action-plan" element={<ActionPlan />} />
        <Route path="/weekly-reports" element={<WeeklyReports />} />
        <Route path="/subscription" element={<Subscription />} />
        <Route path="/subscription/success" element={<SubscriptionSuccess />} />
        <Route path="/invoices" element={<Invoices />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
      
      {/* Admin Routes */}
      <Route element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/organizations" element={<AdminOrganizations />} />
        <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
        <Route path="/admin/invoices" element={<AdminInvoices />} />
        <Route path="/admin/settings" element={<AdminSettings />} />
      </Route>
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
        <CookieConsent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
